// ============================================
// api.js - API Service with Auth & Fingerprint
// ============================================
// Centralized API client with:
// - JWT token management (access + refresh)
// - Automatic token refresh on 401
// - Device fingerprint for anonymous tracking
// - Request/response interceptors

import { getStoredFingerprint } from './fingerprint';

// ============================================
// CONFIGURATION
// ============================================

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Token storage keys
const ACCESS_TOKEN_KEY = 'ghostink_access_token';
const REFRESH_TOKEN_KEY = 'ghostink_refresh_token';
const USER_DATA_KEY = 'ghostink_user_data';

// ============================================
// TOKEN MANAGEMENT
// ============================================

/**
 * Get stored tokens
 */
export function getTokens() {
  return {
    access: localStorage.getItem(ACCESS_TOKEN_KEY),
    refresh: localStorage.getItem(REFRESH_TOKEN_KEY),
  };
}

/**
 * Store tokens
 */
export function setTokens(access, refresh) {
  if (access) localStorage.setItem(ACCESS_TOKEN_KEY, access);
  if (refresh) localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
}

/**
 * Clear all auth data
 */
export function clearAuth() {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_DATA_KEY);
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated() {
  return !!getTokens().access;
}

/**
 * Get stored user data
 */
export function getStoredUser() {
  const data = localStorage.getItem(USER_DATA_KEY);
  if (data) {
    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Store user data
 */
export function setStoredUser(user) {
  if (user) {
    localStorage.setItem(USER_DATA_KEY, JSON.stringify(user));
  } else {
    localStorage.removeItem(USER_DATA_KEY);
  }
}

// ============================================
// TOKEN REFRESH
// ============================================

let isRefreshing = false;
let refreshSubscribers = [];

function subscribeTokenRefresh(cb) {
  refreshSubscribers.push(cb);
}

function onTokenRefreshed(newToken) {
  refreshSubscribers.forEach((cb) => cb(newToken));
  refreshSubscribers = [];
}

/**
 * Refresh access token using refresh token
 */
async function refreshAccessToken() {
  const { refresh } = getTokens();
  if (!refresh) return null;

  if (isRefreshing) {
    // Wait for ongoing refresh
    return new Promise((resolve) => {
      subscribeTokenRefresh((newToken) => resolve(newToken));
    });
  }

  isRefreshing = true;

  try {
    const response = await fetch(`${API_BASE}/accounts/token/refresh/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh }),
    });

    if (response.ok) {
      const data = await response.json();
      localStorage.setItem(ACCESS_TOKEN_KEY, data.access);
      onTokenRefreshed(data.access);
      return data.access;
    } else {
      // Refresh failed - clear auth
      clearAuth();
      return null;
    }
  } catch (e) {
    console.error('Token refresh failed:', e);
    clearAuth();
    return null;
  } finally {
    isRefreshing = false;
  }
}

// ============================================
// CORE REQUEST FUNCTION
// ============================================

/**
 * Make API request with auth and fingerprint
 * @param {string} endpoint - API endpoint (relative or absolute)
 * @param {Object} options - Fetch options
 * @returns {Promise<{data?: any, error?: string, status: number}>}
 */
export async function apiRequest(endpoint, options = {}) {
  const url = endpoint.startsWith('http') ? endpoint : `${API_BASE}${endpoint}`;

  // Get fingerprint for anonymous tracking
  let fpHash = '';
  try {
    const { hash } = await getStoredFingerprint();
    fpHash = hash;
  } catch (e) {
    console.warn('Failed to get fingerprint:', e);
  }

  // Build headers
  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };

  // Add fingerprint header
  if (fpHash) {
    headers['X-Fingerprint-Hash'] = fpHash;
  }

  // Add auth token if available
  let { access } = getTokens();
  if (access) {
    headers['Authorization'] = `Bearer ${access}`;
  }

  try {
    let response = await fetch(url, {
      ...options,
      headers,
    });

    // If 401 and we have a token, try refreshing
    if (response.status === 401 && access) {
      const newToken = await refreshAccessToken();
      if (newToken) {
        headers['Authorization'] = `Bearer ${newToken}`;
        response = await fetch(url, { ...options, headers });
      }
    }

    // Parse response
    let data = null;
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    }

    if (!response.ok) {
      return {
        error: data?.error || data?.detail || data?.message || 'Request failed',
        status: response.status,
        data,
      };
    }

    return { data, status: response.status };
  } catch (e) {
    console.error('API request failed:', e);
    return {
      error: e.message || 'Network error',
      status: 0,
    };
  }
}

// ============================================
// API METHODS
// ============================================

export const api = {
  // ==========================================
  // AUTH ENDPOINTS
  // ==========================================

  /**
   * Register new user
   */
  register: async (data) => {
    const result = await apiRequest('/accounts/register/', {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (result.data?.tokens) {
      setTokens(result.data.tokens.access, result.data.tokens.refresh);
      setStoredUser(result.data.user);
    }

    return result;
  },

  /**
   * Login user
   */
  login: async (email, password) => {
    const result = await apiRequest('/accounts/login/', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });

    if (result.data?.access) {
      setTokens(result.data.access, result.data.refresh);
    }

    return result;
  },

  /**
   * Logout (clear local auth)
   */
  logout: () => {
    clearAuth();
  },

  /**
   * Get current user
   */
  getUser: async () => {
    const result = await apiRequest('/accounts/user/');
    if (result.data) {
      setStoredUser(result.data);
    }
    return result;
  },

  /**
   * Update user profile
   */
  updateUser: (data) =>
    apiRequest('/accounts/user/', {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  /**
   * Change password
   */
  changePassword: (oldPassword, newPassword) =>
    apiRequest('/accounts/user/password/', {
      method: 'PUT',
      body: JSON.stringify({
        old_password: oldPassword,
        new_password: newPassword,
      }),
    }),

  /**
   * Get usage stats (authenticated)
   */
  getAccountUsage: () => apiRequest('/accounts/user/usage/'),

  // ==========================================
  // CHAT ENDPOINTS
  // ==========================================

  /**
   * Send a chat message
   */
  sendMessage: async (content, sessionId = null) => {
    const { fingerprint } = await getStoredFingerprint();

    return apiRequest('/chat/send/', {
      method: 'POST',
      body: JSON.stringify({
        content,
        session_id: sessionId,
        fingerprint,
      }),
    });
  },

  /**
   * Get all sessions
   */
  getSessions: () => apiRequest('/chat/sessions/'),

  /**
   * Get single session with messages
   */
  getSession: (sessionId) => apiRequest(`/chat/sessions/${sessionId}/`),

  /**
   * Update session (title, system_prompt, etc.)
   */
  updateSession: (sessionId, data) =>
    apiRequest(`/chat/sessions/${sessionId}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  /**
   * Delete (archive) session
   */
  deleteSession: (sessionId) =>
    apiRequest(`/chat/sessions/${sessionId}/`, {
      method: 'DELETE',
    }),

  // ==========================================
  // DEVICE & USAGE ENDPOINTS
  // ==========================================

  /**
   * Get usage status (works for both auth and anon)
   */
  getUsageStatus: () => apiRequest('/chat/status/'),

  /**
   * Register device fingerprint
   */
  registerDevice: async () => {
    const { fingerprint } = await getStoredFingerprint();
    return apiRequest('/chat/device/register/', {
      method: 'POST',
      body: JSON.stringify(fingerprint),
    });
  },

  /**
   * Link device to authenticated user
   */
  linkDevice: async () => {
    const { hash } = await getStoredFingerprint();
    return apiRequest('/chat/device/link/', {
      method: 'POST',
      body: JSON.stringify({ fingerprint_hash: hash }),
    });
  },

  // ==========================================
  // HEALTH & MISC
  // ==========================================

  /**
   * Health check
   */
  healthCheck: () => apiRequest('/chat/health/'),

  // ==========================================
  // WRITER ENDPOINTS (from your writer app)
  // ==========================================

  /**
   * Generate essay
   */
  generateEssay: (topic) =>
    apiRequest('/writer/essay/', {
      method: 'POST',
      body: JSON.stringify({ topic }),
    }),

  /**
   * Rewrite text
   */
  rewriteText: (text) =>
    apiRequest('/writer/rewrite/', {
      method: 'POST',
      body: JSON.stringify({ text }),
    }),

  /**
   * Check writer limits
   */
  checkWriterLimits: () => apiRequest('/writer/limits/'),
};

export default api;
